import { DossierDetailsContent } from "@/app/pages/dossier/dossier-details-panel";
import {
    BetrokkenStation,
    BetrokkenStationsBetrokkenheid,
    useDeleteBetrokkenStation,
    useGetDossierBetrokkenStationsById,
} from "@/entities/betrokken-stations";
import { RoleGate, UserRole } from "@/features/auth";
import { useDossierId } from "@/features/dossier-details";
import { Button, Skeleton } from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { useState } from "react";
import { AddBetrokkenStationDialog } from "./add-betrokken-station-dialog";
import { BetrokkenStationsGroup } from "./betrokken-stations-group";

export function BetrokkenStationsDetails() {
    const id = useDossierId();
    const { data: betrokkenStations, isPending, error } = useGetDossierBetrokkenStationsById(id);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const {
        mutate: deleteBetrokkenStation,
        isPending: deletePending,
        variables: deleteVariables,
        error: deleteError,
    } = useDeleteBetrokkenStation();

    if (isPending) {
        return (
            <DossierDetailsContent data-testid="betrokken-stations-skeleton">
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-32 w-full" />
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-32 w-full" />
                </div>
            </DossierDetailsContent>
        );
    }

    if (error) {
        return <p>Fout bij het laden van betrokken stations: {error.message}</p>;
    }

    if (!betrokkenStations) {
        return (
            <DossierDetailsContent data-testid="betrokken-stations-empty">
                <p className="text-content-secondary">Geen betrokken stations voor dit dossier.</p>
            </DossierDetailsContent>
        );
    }

    const flattenedStations = betrokkenStations.flatMap((station) =>
        station.betrokkenheid.map((betrokkenheidType) => ({ betrokkenheidType, station }))
    );
    const grouped = Object.groupBy(flattenedStations, (item) => item.betrokkenheidType);

    // Sort groups by enum order
    const enumOrder = Object.values(BetrokkenStationsBetrokkenheid);
    const sortedGroups = enumOrder.reduce<[BetrokkenStationsBetrokkenheid, BetrokkenStation[]][]>((acc, type) => {
        if (grouped[type]) {
            acc.push([type, grouped[type].map((item) => item.station)]);
        }
        return acc;
    }, []);

    const hasStations = betrokkenStations.length > 0;

    return (
        <DossierDetailsContent>
            <RoleGate roles={[UserRole.REGIEVOERDER]}>
                <div className="mb-6 flex justify-end">
                    <Button
                        variant="secondary"
                        icon="station"
                        iconSize="m"
                        iconStrokeWidth="thick"
                        onClick={() => setIsAddDialogOpen(true)}
                    >
                        Stations toevoegen
                    </Button>
                </div>
            </RoleGate>
            <div className="space-y-6">
                {!hasStations && (
                    <div className="bg-surface-secondary flex flex-col items-center justify-center rounded-lg p-12 text-center">
                        <p className="text-content-secondary text-lg">Geen betrokken stations gevonden voor dit dossier.</p>
                    </div>
                )}

                {hasStations && (
                    <div className="space-y-6">
                        {deleteError && <ErrorDisplay error={deleteError} title="Fout bij het verwijderen van station" />}
                        {sortedGroups.map(([betrokkenheidType, stations]) => (
                            <BetrokkenStationsGroup
                                key={betrokkenheidType}
                                betrokkenheidType={betrokkenheidType}
                                stations={stations}
                                onRemoveStation={(stationCode) => deleteBetrokkenStation({ dossierNumber: id, stationCode })}
                                removingStationCode={deletePending ? deleteVariables.stationCode : undefined}
                            />
                        ))}
                    </div>
                )}
            </div>
            <AddBetrokkenStationDialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} dossierNumber={id} />
        </DossierDetailsContent>
    );
}
